import React, { useEffect, useState, useContext, useMemo } from 'react';
import axios from 'axios';
import SingleItemDetailTemplate from '../../../Reusable_Components/SingleItemDetailTemplate';
import ConfirmModal from '../../../Reusable_Components/ConfirmModal';
import ConvertActionButton from '../../../Reusable_Components/ConvertActionButton';
import { useParams, useNavigate } from 'react-router-dom';
import { API_ROUTES, API_DOMAIN, headers } from '../../../Reusable_Components/Config/apiConfig';
import { ToastContext } from '../../../../context/ToastContext';
import useDetailFetch from '../../../../hooks/useDetailFetch';
import { fmtDate } from '../../../../utils/fmt';

const SingleQuoteDetails = () => {
  const { quoteId: id } = useParams();
  const navigate = useNavigate();
  const { showToast } = useContext(ToastContext);

  const decodedId = id ? atob(id) : '';
  const quoteId = decodedId.replace(/\D/g, '');

  const [showDelete, setShowDelete] = useState(false);
  const [pendingApproval, setPendingApproval] = useState(null);
  const [parentOpp, setParentOpp] = useState(null);

  const url = quoteId ? `${API_DOMAIN}${API_ROUTES.quote}${quoteId}/` : null;
  const { data: details, loading, refetch } = useDetailFetch(url);

  const columns = useMemo(() => {
    if (!details) return [];
    const excluded = ['id', 'updated_at', 'created_at'];
    return Object.keys(details)
      .filter(c => !excluded.includes(c))
      .map(c => ({ label: c.charAt(0).toUpperCase() + c.slice(1).replace(/_/g, ' '), field: c }));
  }, [details]);

  const fetchPendingApproval = async () => {
    try {
      const res = await axios.get(`${API_DOMAIN}quote/approvals/`, { headers });
      const list = Array.isArray(res.data) ? res.data : (res.data?.results || res.data?.data || []);
      const pending = list.find(a => String(a.quote) === String(quoteId) && a.status === 'pending');
      setPendingApproval(pending || null);
    } catch (e) { setPendingApproval(null); }
  };

  useEffect(() => {
    fetchPendingApproval();
  }, [quoteId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!details?.opportunity) { setParentOpp(null); return; }
    axios.get(`${API_DOMAIN}${API_ROUTES.opportunity}${details.opportunity}/`, { headers })
      .then(res => setParentOpp(res.data))
      .catch(() => setParentOpp(null));
  }, [details?.opportunity]); // eslint-disable-line

  const handleDelete = async () => {
    try {
      await axios.delete(`${API_DOMAIN}${API_ROUTES.quote}${quoteId}/`, { headers });
      showToast('Quote deleted successfully', 'success');
      navigate('/quote/list');
    } catch (error) {
      console.error('Error deleting quote:', error);
      showToast('Error deleting quote', 'error');
    }
  };

  const refresh = () => { refetch(); fetchPendingApproval(); };
  const isArchived = details?.archived === true; // best-effort; backend guards the real double-archive case regardless

  return (
    <div>
      {!loading && details && (
        <div className="card" style={{ marginBottom: 16 }}>
          <div className="card-header"><span className="card-title">Quote Lifecycle</span></div>
          <div className="card-body" style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
            {parentOpp && (
              <button
                className="btn btn-ghost"
                style={{ gap: 8 }}
                onClick={() => navigate(`/opportunity/details/${btoa(parentOpp.id)}`)}
              >
                💼 From: {parentOpp.opportunity_name}
              </button>
            )}
            {pendingApproval ? (
              <>
                <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>Pending approval since {fmtDate(pendingApproval.created_at)}</span>
                <ConvertActionButton
                  label="Approve"
                  url={`${API_DOMAIN}quote/approvals/${pendingApproval.id}/decide/`}
                  body={{ decision: 'approved' }}
                  onDone={refresh}
                />
                <ConvertActionButton
                  label="Reject"
                  url={`${API_DOMAIN}quote/approvals/${pendingApproval.id}/decide/`}
                  body={{ decision: 'rejected' }}
                  onDone={refresh}
                  className="btn btn-danger"
                />
              </>
            ) : (
              <ConvertActionButton
                label="Submit for Approval"
                url={`${API_DOMAIN}quote/quotes/${quoteId}/submit-for-approval/`}
                body={{}}
                onDone={refresh}
              />
            )}
            <ConvertActionButton
              label="Record Payment"
              url={`${API_DOMAIN}quote/quotes/${quoteId}/record-payment/`}
              body={() => {
                const amount = window.prompt('Payment amount (KES)?', details.line_items?.length ? '' : '0');
                if (amount === null) return null;
                return { payment_date: new Date().toISOString().slice(0, 10), payment_amount: amount, payment_method: 'bank_transfer', payment_status: 'completed' };
              }}
              onDone={refresh}
              className="btn btn-secondary"
            />
            <ConvertActionButton
              label="Archive"
              url={`${API_DOMAIN}quote/quotes/${quoteId}/archive/`}
              body={{}}
              onDone={refresh}
              className="btn btn-ghost"
              disabled={isArchived}
            />
          </div>
        </div>
      )}
      <SingleItemDetailTemplate
        details={details}
        columns={columns}
        loading={loading}
        entityName="Quote"
        listPath="/quote/list"
        editPath={`/quote/input/${btoa(quoteId)}`}
        onDelete={() => setShowDelete(true)}
      />
      <ConfirmModal
        isOpen={showDelete}
        title="Delete Quote"
        message="Are you sure you want to delete this quote?"
        onConfirm={handleDelete}
        onCancel={() => setShowDelete(false)}
      />
    </div>
  );
};

export default SingleQuoteDetails;
